import React from "react";
import { Link } from "react-router-dom";
import { inkFor, tintFor } from "../../utils/themeColor";

/**
 * MenuCard — vertical card tile for the dashboard's Quick Actions and
 * Departments grids.  Inspired by the Menutech reference: each card has
 * a large colored icon container at the top, a label and optional
 * subtitle below, and an arrow indicator in the top-right corner.
 *
 * Distinct from ModuleShortcutCard (which is horizontal and is used on
 * module landing pages). This one is intentionally a separate component
 * so changing its layout doesn't ripple into Supervisor/Production/Sales
 * landing pages.
 *
 * Props
 *  - icon:     ri-* / bx-* / mdi-* icon class
 *  - label:    headline text (required)
 *  - subtitle: helper line under the label
 *  - tone:     "primary" | "info" | "success" | "warning" | "danger" |
 *              "secondary" | "dark" — drives the bg + fg colors of the
 *              icon container. Defaults to "primary".
 *  - to:       router path. When set, the card is a <Link>.
 *  - onClick:  click handler when no `to`.
 *  - disabled: bool. Soft-disables; the card is rendered but inert and
 *              shows a small lock instead of the arrow.
 *  - disabledReason: tooltip on the lock.
 */
const TONE_FALLBACK = "primary";

// Tone → color set. `bg`/`fg` kept for the count pill and backward
// compatibility; Round 16 adds `solid`+`deep` gradient stops for the
// icon tile (owner feedback: the IT page's saturated tiles with white
// icons read as more premium than the old pastel squares).
const TONE_COLORS = {
    primary:   { bg: "#eef1fa", fg: "#5b50d6", solid: "#5b50d6", deep: "#4338ca" },
    info:      { bg: "#e3f4f9", fg: "#1893a8", solid: "#1893a8", deep: "#0f7285" },
    success:   { bg: "#e3f5ec", fg: "#1f9d55", solid: "#1f9d55", deep: "#15803d" },
    warning:   { bg: "#fdf2e3", fg: "#f7b84b", solid: "#d49423", deep: "#b45309" },
    danger:    { bg: "#fbe5e5", fg: "#d65f5f", solid: "#d65f5f", deep: "#b91c1c" },
    secondary: { bg: "#eef0f4", fg: "#6c757d", solid: "#6c757d", deep: "#4b5563" },
    dark:      { bg: "#dfe2e8", fg: "#212529", solid: "#343a40", deep: "#1f2937" },
};

const MenuCard = ({
    icon = "ri-stack-line",
    label,
    subtitle,
    tone = TONE_FALLBACK,
    to,
    onClick,
    disabled = false,
    disabledReason,
    count,
}) => {
    const colors = TONE_COLORS[tone] || TONE_COLORS[TONE_FALLBACK];

    const card = (
        <div
            className={`mc-card ${disabled ? "mc-card--disabled" : ""}`}
            style={{
                background: "var(--ssw-surface, #ffffff)",
                border: "1px solid var(--ssw-border, #e9ecf3)",
                borderRadius: 16,
                padding: 20,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.55 : 1,
                textDecoration: "none",
                color: "inherit",
                position: "relative",
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05)",
                transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
            }}
        >
            {/* Icon container — the "hero" element. Round 16: saturated
                gradient tile with a white icon + soft tone glow (matches
                the IT-page shortcut tiles the owner preferred over the
                old pastel squares). */}
            <div
                aria-hidden="true"
                className="mc-card__icon"
                data-tone={tone || TONE_FALLBACK}
                style={{
                    width: 54,
                    height: 54,
                    borderRadius: 14,
                    background: `linear-gradient(135deg, ${colors.solid} 0%, ${colors.deep} 100%)`,
                    color: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 14,
                    fontSize: 25,
                    flexShrink: 0,
                    boxShadow: `0 8px 16px -8px ${colors.solid}99`,
                }}
            >
                <i className={icon} />
            </div>

            {/* Title + optional count pill + subtitle */}
            <h6
                className="mb-1 mc-card__title"
                style={{ color: "var(--ssw-text-1, #1c2333)", fontSize: 15.5, fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1.3, display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}
            >
                {label}
                {typeof count === "number" && count > 0 ? (
                    <span
                        aria-label={`${count} actions`}
                        style={{
                            fontSize: 11,
                            fontWeight: 700,
                            lineHeight: 1,
                            padding: "3px 7px",
                            borderRadius: 999,
                            background: tintFor(colors.bg),
                            color: inkFor(colors.deep),
                        }}
                    >
                        {count}
                    </span>
                ) : null}
            </h6>
            {subtitle ? (
                <p
                    className="mb-0"
                    style={{ fontSize: 13, lineHeight: 1.45, color: "var(--ssw-text-2, #66708a)" }}
                >
                    {subtitle}
                </p>
            ) : null}

            {/* Top-right indicator (arrow when active, lock when disabled) */}
            <span
                aria-hidden="true"
                className="mc-card__indicator"
                style={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: disabled ? "var(--ssw-surface-2, #eef0f4)" : "var(--ssw-surface-2, #f4f6fb)",
                    color: disabled ? "var(--ssw-text-2, #9ba3b5)" : "var(--ssw-text-2, #5a6378)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    transition: "background 0.18s ease, color 0.18s ease, transform 0.18s ease",
                }}
                title={disabled ? (disabledReason || "Locked") : undefined}
            >
                <i className={disabled ? "ri-lock-2-line" : "ri-arrow-right-s-line"} />
            </span>
        </div>
    );

    if (disabled) {
        return (
            <>
                {card}
                <MenuCardStyles solid={colors.solid} />
            </>
        );
    }

    if (to) {
        return (
            <>
                <Link to={to} style={{ display: "block", height: "100%", textDecoration: "none" }} aria-label={label}>
                    {card}
                </Link>
                <MenuCardStyles solid={colors.solid} />
            </>
        );
    }

    return (
        <>
            <div
                onClick={onClick}
                role={onClick ? "button" : undefined}
                tabIndex={onClick ? 0 : undefined}
                onKeyDown={onClick ? (e) => {
                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); }
                } : undefined}
                style={{ height: "100%" }}
            >
                {card}
            </div>
            <MenuCardStyles solid={colors.solid} />
        </>
    );
};

// Hover styles — emitted as inline <style> so the component is fully
// self-contained. (Putting them in a CSS file would mean another import.)
const MenuCardStyles = () => (
    <style>{`
        .mc-card:not(.mc-card--disabled):hover {
            transform: translateY(-3px);
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
            border-color: rgba(91, 80, 214, 0.20) !important;
        }
        .mc-card:not(.mc-card--disabled):hover .mc-card__indicator {
            background: var(--ssw-primary-fill, #5b50d6) !important;
            color: #ffffff !important;
            transform: translateX(2px);
        }
        .mc-card:not(.mc-card--disabled):hover .mc-card__title {
            color: var(--ssw-ink-indigo, #2c2076) !important;
        }
    `}</style>
);

export default React.memo(MenuCard);
